import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  X,
  Download,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  FileImage,
  FileVideo,
  FileText,
  File,
  Figma,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';

const FILE_TYPE_ICONS = {
  image: FileImage,
  video: FileVideo,
  document: FileText,
  design: Figma,
  unknown: File
};

const ImageViewer = ({ src, alt }) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  const handleZoomIn = () => setZoom(z => Math.min(z + 0.25, 3));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.25, 0.5));
  const handleRotate = () => setRotation(r => (r + 90) % 360);
  const handleReset = () => { setZoom(1); setRotation(0); };

  return (
    <div className="relative flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-center gap-2 p-2 border-b border-slate-200 bg-slate-50">
        <Button variant="outline" size="sm" onClick={handleZoomOut}>
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-sm text-slate-600 w-16 text-center">{Math.round(zoom * 100)}%</span>
        <Button variant="outline" size="sm" onClick={handleZoomIn}>
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={handleRotate}>
          <RotateCw className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={handleReset}>
          <Maximize2 className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Image Container */}
      <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center p-4">
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-full object-contain transition-transform duration-200"
          style={{
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
          }}
          data-testid="preview-image"
        />
      </div>
    </div>
  );
};

const VideoPlayer = ({ src, mimeType }) => {
  return (
    <div className="flex items-center justify-center h-full bg-black p-4">
      <video
        src={src}
        controls
        className="max-w-full max-h-full"
        data-testid="preview-video"
      >
        <source src={src} type={mimeType || 'video/mp4'} />
        Your browser does not support the video tag.
      </video>
    </div>
  );
};

const PDFViewer = ({ src }) => {
  return (
    <div className="h-full w-full">
      <iframe
        src={src}
        className="w-full h-full border-0"
        title="PDF Preview"
        data-testid="preview-pdf"
      />
    </div>
  );
};

const FigmaEmbed = ({ url }) => {
  // Convert Figma URL to embed URL
  const getEmbedUrl = (figmaUrl) => {
    if (figmaUrl.includes('figma.com/file/')) {
      const fileId = figmaUrl.split('/file/')[1]?.split('/')[0];
      if (fileId) {
        return `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(figmaUrl)}`;
      }
    }
    return figmaUrl;
  };

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex items-center justify-between p-2 bg-slate-50 border-b">
        <span className="text-sm text-slate-600 flex items-center gap-2">
          <Figma className="w-4 h-4" />
          Figma Design
        </span>
        <Button variant="outline" size="sm" asChild>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-4 h-4 mr-2" />
            Open in Figma
          </a>
        </Button>
      </div>
      <iframe
        src={getEmbedUrl(url)}
        className="flex-1 w-full border-0"
        allowFullScreen
        title="Figma Preview"
        data-testid="preview-figma"
      />
    </div>
  );
};

const UnsupportedFile = ({ fileType, fileName, fileUrl }) => {
  const Icon = FILE_TYPE_ICONS[fileType] || File;
  
  return (
    <div className="h-full flex flex-col items-center justify-center p-8 text-center">
      <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
        <Icon className="w-10 h-10 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 mb-2">
        Preview not available
      </h3>
      <p className="text-slate-500 mb-4 max-w-md">
        This file type cannot be previewed in the browser. Please download it to view.
      </p>
      <Button asChild>
        <a href={fileUrl} download={fileName}>
          <Download className="w-4 h-4 mr-2" />
          Download File
        </a>
      </Button>
    </div>
  );
};

export const MediaPreview = ({ 
  asset, 
  open, 
  onClose, 
  onApprove, 
  onReject,
  canApprove = false,
  isApproving = false
}) => {
  if (!asset) return null;

  const { name, file_url, file_type, mime_type, figma_url, file_size, status } = asset;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // Build full URL for file
  const fullFileUrl = file_url?.startsWith('/api') 
    ? `${backendUrl}${file_url}` 
    : file_url;

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const renderPreview = () => {
    // Handle Figma links
    if (figma_url) {
      return <FigmaEmbed url={figma_url} />;
    }

    // Handle by file type
    switch (file_type) {
      case 'image':
        return <ImageViewer src={fullFileUrl} alt={name} />;
      case 'video':
        return <VideoPlayer src={fullFileUrl} mimeType={mime_type} />;
      case 'document':
        if (mime_type === 'application/pdf' || file_url?.endsWith('.pdf')) {
          return <PDFViewer src={fullFileUrl} />;
        }
        return <UnsupportedFile fileType={file_type} fileName={name} fileUrl={fullFileUrl} />;
      default:
        return <UnsupportedFile fileType={file_type} fileName={name} fileUrl={fullFileUrl} />;
    }
  };

  const Icon = FILE_TYPE_ICONS[file_type] || File;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[85vh] p-0 flex flex-col" data-testid="media-preview-modal">
        <DialogHeader className="p-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Icon className="w-5 h-5 text-slate-500" />
              </div>
              <div>
                <DialogTitle className="text-left">{name}</DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-xs">
                    {file_type}
                  </Badge>
                  <span className="text-xs text-slate-500">
                    {formatFileSize(file_size)}
                  </span>
                  {status && (
                    <Badge 
                      className={`text-xs ${
                        status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                        status === 'rejected' ? 'bg-red-50 text-red-700' :
                        status === 'in_review' ? 'bg-purple-50 text-purple-700' :
                        'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {status}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {fullFileUrl && (
                <Button variant="outline" size="sm" asChild>
                  <a href={fullFileUrl} download={name}>
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </a>
                </Button>
              )}
              {fullFileUrl && !fullFileUrl.startsWith('blob:') && (
                <Button variant="outline" size="sm" asChild>
                  <a href={fullFileUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open
                  </a>
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>
        
        {/* Preview Area */}
        <div className="flex-1 overflow-hidden">
          {renderPreview()}
        </div>
        
        {/* Approval Actions */}
        {canApprove && status === 'in_review' && (
          <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-3 bg-slate-50">
            <Button
              variant="outline"
              onClick={() => onReject?.(asset.id)}
              disabled={isApproving}
              className="text-red-600 hover:bg-red-50"
              data-testid="preview-reject-btn"
            >
              {isApproving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Reject
            </Button>
            <Button
              onClick={() => onApprove?.(asset.id)}
              disabled={isApproving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="preview-approve-btn"
            >
              {isApproving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Approve
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
